import i18n from '../lib/i18n'
import { Sentry } from '../lib/sentry'

export const API_URL = import.meta.env.VITE_API_URL ?? 'https://api.caliuapp.com'

// Per-device identifier for rate limiting fairness across web/iOS/macOS.
// Generated once and persisted in localStorage.
function getDeviceId(): string {
  const key = 'caliu_device_id'
  let id = localStorage.getItem(key)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(key, id)
  }
  return id
}

export class ApiError extends Error {
  status: number
  code?: string

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

// ── 401 handling ─────────────────────────────────────────────────────────────
// Lazy-imported to avoid circular dependency (auth store → api/client → auth store).
// Debounced so concurrent 401s don't trigger multiple force-logouts.

let _forceLogoutInFlight = false

async function handleUnauthorized(): Promise<void> {
  if (_forceLogoutInFlight) return
  _forceLogoutInFlight = true
  try {
    // Dynamic import breaks the circular reference
    const { useAuthStore } = await import('../store/auth')
    // No server-backed account — a stray 401 must never wipe local data
    // (it may be the only copy). forceLogout guards this too; checking here
    // keeps the trigger itself safe.
    if (useAuthStore.getState().user === null) return
    await useAuthStore.getState().forceLogout()
  } finally {
    _forceLogoutInFlight = false
  }
}

/**
 * A server `402 Payment Required` means a Caliu Pro gate was hit (storage cap,
 * API-key limit, MCP, …). Surface the paywall — a safety net for any
 * Pro feature not already gated client-side. Dynamic import breaks the cycle.
 */
async function handlePaymentRequired(feature: 'server'): Promise<void> {
  try {
    const { useSubscriptionStore } = await import('../store/subscription')
    // The server disagrees with a client that counts this account as Pro: ask
    // it again. Selling Pro to someone who has it is never the answer; only a
    // fresh "not Pro" opens the plans.
    if (useSubscriptionStore.getState().isPro) {
      const ent = await useSubscriptionStore.getState().refresh()
      if (!ent || ent.isPro) return
    }
    const { useUIStore } = await import('../store/ui')
    useUIStore.getState().openPaywall(feature)
  } catch {
    // store unavailable (e.g. teardown) — ignore
  }
}

/** Paths that should never trigger a force-logout on 401 (auth endpoints). */
const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/me', '/auth/logout']

function isAuthPath(path: string): boolean {
  return AUTH_PATHS.some((p) => path.startsWith(p))
}

// Client identity sent on every request as `<platform>/<version>`. Web always
// ships the current bundle, so a single sentinel version is enough.
const CLIENT_HEADER = 'web/1.0.0'
const SYNC_LEVEL = '3'

/**
 * Standard headers + credentials for a raw `fetch` against the API. Exported
 * for the rare caller that must bypass the global 401/402 hooks (the transfer
 * claim pump handles those statuses itself).
 */
export function buildInit(options?: RequestInit): RequestInit {
  return {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Caliu-Device-Id': getDeviceId(),
      'X-Caliu-Client': CLIENT_HEADER,
      // Sync level 3 (PLAN.md D9): this build runs the content engine and
      // understands notes shared with people. The worker answers today's
      // contract unless the account's syncV2 is on.
      'X-Caliu-Sync': SYNC_LEVEL,
      // Selected in-app language so the worker localizes responses + emails to
      // match it (not the browser default). Source matches lib/format.ts.
      'Accept-Language': i18n.resolvedLanguage || i18n.language || 'en',
      ...options?.headers,
    },
    credentials: 'include',
  }
}

async function parseApiError(res: Response): Promise<ApiError> {
  let message = `API error ${res.status}`
  let code: string | undefined
  try {
    const body = await res.json() as { error?: string; code?: string }
    if (body.error) message = body.error
    if (body.code) code = body.code
  } catch {
    // ignore parse errors
  }
  return new ApiError(message, res.status, code)
}

/**
 * Fire-and-forget API call for local-first code paths. Never throws.
 *
 * - Network errors (offline): silent, returns null.
 * - HTTP errors when online: logged to console, returns null.
 */
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(`${API_URL}${path}`, buildInit(options))
    // DELETE + 404 = resource already gone = success (don't parse body — may not be JSON)
    if (options?.method === 'DELETE' && res.status === 404) {
      return null
    }
    if (!res.ok) {
      // Session expired — force-logout (skip auth endpoints to avoid loops)
      if (res.status === 401 && !isAuthPath(path)) {
        handleUnauthorized()
        return null
      }
      // Caliu Pro gate hit server-side — surface the paywall.
      if (res.status === 402) {
        void handlePaymentRequired('server')
        return null
      }
      if (navigator.onLine) {
        const err = await parseApiError(res)
        console.error(`[api] ${options?.method ?? 'GET'} ${path} → ${res.status}: ${err.message}`)
        if (res.status >= 500) {
          Sentry.captureException(err)
        }
      }
      return null
    }
    return await res.json() as T
  } catch (err) {
    // Network error — only log if we're online (unexpected failure)
    if (navigator.onLine) {
      console.error(`[api] ${options?.method ?? 'GET'} ${path} failed (network)`)
      Sentry.captureException(err)
    }
    return null
  }
}

/**
 * API call that throws on error. Use for user-initiated actions where the UI
 * needs the error message (auth, sharing, API keys, etc.).
 */
export async function apiFetchOrThrow<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, buildInit(options))

  // DELETE + 404 = resource already gone = success (don't parse body — may not be JSON)
  if (options?.method === 'DELETE' && res.status === 404) {
    return undefined as T
  }

  if (!res.ok) {
    // Session expired — force-logout and throw (skip auth endpoints to avoid loops)
    if (res.status === 401 && !isAuthPath(path)) {
      handleUnauthorized()
    }
    // Caliu Pro gate hit server-side — surface the paywall (still throws so the
    // caller can show its own error too).
    if (res.status === 402) {
      void handlePaymentRequired('server')
    }
    throw await parseApiError(res)
  }

  return res.json() as Promise<T>
}
